// src/features/mocktests/WritingSection.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTestNavigation } from "../../../utils/testNavigation";
import {
  Box,
  Typography,
  Dialog,
  Button,
  TextField,
  Backdrop,
  Card,
  IconButton,
  Badge,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import {
  Visibility,
  VisibilityOff,
  CheckRounded as CheckRoundedIcon,
  AccessTime as AccessTimeIcon,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import {
  useCreateWritingAnswerMutation,
  useUploadWritingImagesMutation,
  useLazyGetTestAttemptResultsQuery,
} from "../mockTestSlice";
import NotesModal from "./NotesModal";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Header Toggle Icon Component
--------------------------------------------------------------- */

const HeaderToggleIcon = ({ isHeaderVisible, onToggle }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        py: 1,
        bgcolor: "rgba(247, 247, 247, 0.95)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(157, 34, 53, 0.1)",
        position: "sticky",
        top: 0,
        zIndex: 999,
      }}
    >
      <Tooltip
        title={isHeaderVisible ? "Hide Header" : "Show Header"}
        placement="bottom"
        arrow
      >
        <IconButton
          onClick={onToggle}
          sx={{
            background: "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
            color: "white",
            width: 40,
            height: 40,
            boxShadow: "0px 4px 16px rgba(157, 34, 53, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
              transform: "translateY(-1px)",
              boxShadow: "0px 6px 20px rgba(157, 34, 53, 0.4)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
        >
          {isHeaderVisible ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Add custom scrollbar styling
const scrollbarStyles = `
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #CBD5E0 #F7FAFC;
  }
  
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #F7FAFC;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #CBD5E0;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #A0AEC0;
  }
  
  /* Ensure responsive layout */
  .writing-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    width: 100vw;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

// Add style tag to head of document with better error handling
if (typeof document !== "undefined" && typeof window !== "undefined") {
  // Remove existing style if it exists
  const existingStyle = document.getElementById("writing-section-styles");
  if (existingStyle) {
    existingStyle.remove();
  }

  const styleEl = document.createElement("style");
  styleEl.id = "writing-section-styles";
  styleEl.textContent = scrollbarStyles;
  document.head.appendChild(styleEl);
}

/* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
const EnhancedCountdown = ({
  size = 56,
  stroke = 5,
  progress = 0, // 0..100
  showPie = true, // show inside pie fill
  showRing = true, // show outer progress ring
  idSuffix = "writing",
}) => {
  const p = Math.min(100, Math.max(0, progress));
  const finished = p >= 100;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c;
  const offset = c * (1 - p / 100);

  // ------- PIE PATH (sector fill) -------
  const angle = (p / 100) * Math.PI * 2;
  const cx = size / 2;
  const cy = size / 2;
  const x = cx + r * Math.cos(-Math.PI / 2 + angle);
  const y = cy + r * Math.sin(-Math.PI / 2 + angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const piePath =
    p <= 0
      ? ""
      : `
    M ${cx} ${cy}
    L ${cx} ${cy - r}
    A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
    Z
  `;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D2235" />
            <stop offset="100%" stopColor="#5d1220" />
          </linearGradient>
          <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
            <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
          </radialGradient>
        </defs>

        {/* Soft background plate for a card-ish look */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#soft-${idSuffix})`} />

        {/* Track */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(157,34,53,0.18)"
            strokeWidth={stroke}
          />
        )}

        {/* Pie fill from center (sector) */}
        {showPie && p > 0 && (
          <path d={piePath} fill={`url(#grad-${idSuffix})`} />
        )}

        {/* Progress Ring on top */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#grad-${idSuffix})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {/* Subtle glow when finished */}
        {finished && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9D2235"
            strokeOpacity="0.25"
            strokeWidth={2}
          />
        )}
      </svg>

      {/* Center icon */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 180ms ease",
          transform: finished ? "scale(1.05)" : "scale(1)",
        }}
      >
        {finished ? (
          <CheckRoundedIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        ) : (
          <AccessTimeIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        )}
      </Box>
    </Box>
  );
};

export default forwardRef(function WritingSection(
  {
    writingData,
    answer,
    onAnswerChange,
    onTimeUp,
    onSubmit,
    timer = undefined,
    mode = "simulation",
    hideSubmitButton = false,
    submissionType = "computer", // new prop: "computer" | "paper"
    studentAttemptId = "", // new prop for API
    writingTestId = "", // new prop for writing test ID
    isAiBased = true, // new prop for evaluation type
    isSubmitted = false, // new prop for submission lock status
    onResultsReceived = null, // new prop for handling results callback
    shouldFetchResults = true, // new prop to control whether to fetch results
    startTime = null, // ISO string from API
    endTime = null, // ISO string from API
    onEndTimeAdjustment = null, // Callback to notify parent of adjusted end time
    onBack = null, // Function to handle back navigation
    isLastQuestion = false, // Whether this is the last question in practice mode
    onFinish = null, // Function to handle finish action
    updateTestAttempt = null, // Function to update test attempt (for simulation mode)
    testId = null, // Test ID for browser back navigation
    isHeaderVisible = true, // New prop to control notes button visibility
    // New props for footer control
    onConfirmSubmit = null, // Function to handle confirm submit from parent
    footerProps = null, // Object containing footer-related state and functions
  },
  ref
) {
  const { caseNotes = [], writingPrompt } = writingData;

  // Extract reading time from instructions (default to 5 minutes if not found)
  const getReadingTimeFromInstructions = () => {
    const instructionsText = writingData?.instructions || "";
    const readingTimeMatch = instructionsText.match(
      /READING TIME:\s*(\d+)\s*MINUTES?/i
    );
    return readingTimeMatch ? parseInt(readingTimeMatch[1]) : 5; // Default to 5 minutes
  };

  // Notes functionality
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  // Header visibility state
  const [headerVisible, setHeaderVisible] = useState(true);

  // ----- Zoom state for left (case notes) and right (writing task) panels -----
  const [leftZoom, setLeftZoom] = useState(1);
  const [rightZoom, setRightZoom] = useState(1);

  // Zoom functions
  const handleLeftZoomIn = () => {
    setLeftZoom((prev) => Math.min(prev + 0.1, 1.5));
  };
  const handleLeftZoomOut = () => {
    setLeftZoom((prev) => Math.max(prev - 0.1, 0.8));
  };
  const handleRightZoomIn = () => {
    setRightZoom((prev) => Math.min(prev + 0.1, 1.5));
  };
  const handleRightZoomOut = () => {
    setRightZoom((prev) => Math.max(prev - 0.1, 0.8));
  };

  const handleAddNote = (noteData) => {
    setNotes((prevNotes) => [
      ...prevNotes,
      {
        ...noteData,
        timestamp: new Date().toISOString(),
        part: "Writing Task",
      },
    ]);
  };

  // Preparation time logic with API integration
  const PREP_TIME_MINUTES = getReadingTimeFromInstructions();
  const PREP_TIME_SECONDS = PREP_TIME_MINUTES * 60;
  const [prepRemaining, setPrepRemaining] = useState(PREP_TIME_SECONDS);
  const [prepActive, setPrepActive] = useState(true);
  const [skippedTime, setSkippedTime] = useState(0); // Time skipped in seconds
  const [adjustedEndTime, setAdjustedEndTime] = useState(endTime); // Adjusted end time after skipping

  // Navigation hook and test type utilities
  const navigate = useNavigate();
  const { baseRoute, testType } = useTestNavigation();

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    if (!testId) return; // Only if testId is provided

    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Navigate to main mock test screen using React Router
      navigate(`${baseRoute}/${testId}`, { replace: true });
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, navigate, baseRoute]);

  // Timer effect for API-based timing (startTime/endTime)
  const [timerPaused, setTimerPaused] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Text highlighting & notes functionality now handled by TextHighlighterWrapper

  // Modal & progress state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submissionError, setSubmissionError] = useState(null);
  const intervalRef = useRef(null);

  // For paper-based: image upload
  const [uploadedImages, setUploadedImages] = useState([]);

  // API hooks
  const [createWritingAnswer] = useCreateWritingAnswerMutation();
  const [uploadWritingImages] = useUploadWritingImagesMutation();
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();

  const handleImageUpload = (event) => {
    // Prevent upload during preparation time
    if (prepActive) {
      alert(
        "Image upload is not available during preparation time. Please wait for preparation to end or skip it."
      );
      return;
    }

    const files = Array.from(event.target.files);

    // If already 2 or more images uploaded, prevent further upload
    if (uploadedImages.length >= 2) {
      alert("You can upload only 2 images.");
      return;
    }

    // Limit new uploads to 2 - existing count
    const allowedCount = 2 - uploadedImages.length;
    const limitedFiles = files.slice(0, allowedCount);

    const newImages = limitedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setUploadedImages((prev) => [...prev, ...newImages]);
  };

  const handleRemoveImage = (indexToRemove) => {
    setUploadedImages(uploadedImages.filter((_, idx) => idx !== indexToRemove));
  };

  // Submit logic (calls onSubmit with either text or images)
  const handleConfirmSubmit = useCallback(async () => {
    setConfirmOpen(false);
    setSubmissionError(null);
    setUploading(true);
    setUploadProgress(0);
    intervalRef.current = setInterval(
      () => setUploadProgress((p) => Math.min(p + 5, 100)),
      200
    );

    try {
      if (submissionType === "computer") {
        // For computer-based submission, send to writing API
        console.log("Props received:", {
          studentAttemptId,
          writingTestId,
          isAiBased,
        });

        const payload = {
          studentTestAttemptId: studentAttemptId,
          writingTestId: writingTestId,
          writingText: answer, // Make sure `answer` is correctly updated via ref or controlled state
          isAiBased: isAiBased,
        };

        console.log("Submitting writing answer:", payload);

        // Call the actual API
        const result = await createWritingAnswer(payload).unwrap();
        console.log("Writing submission successful:", result);

        // After successful submission, fetch test attempt results only if needed
        if (shouldFetchResults) {
          console.log("Fetching test attempt results for:", studentAttemptId);
          try {
            const resultsResponse = await getTestAttemptResults(
              studentAttemptId
            ).unwrap();
            console.log("Test attempt results:", resultsResponse);
            // Call the callback if provided
            if (onResultsReceived) {
              onResultsReceived(resultsResponse);
            }
          } catch (resultsError) {
            console.error("Error fetching test attempt results:", resultsError);
          }
        } else {
          console.log(
            "Skipping test results fetch - not needed for this submission"
          );
        }
      } else if (submissionType === "paper") {
        // For paper-based submission, first upload images, then submit
        console.log("Paper-based submission - uploading images first");

        if (uploadedImages.length === 0) {
          throw new Error("No images uploaded for paper-based submission");
        }

        // Prepare FormData for image upload
        const formData = new FormData();
        uploadedImages.forEach((img) => {
          formData.append("writingImages", img.file);
        });

        console.log("Uploading images:", uploadedImages.length, "files");

        // Upload images first
        const uploadResult = await uploadWritingImages(formData).unwrap();
        console.log("Image upload successful:", uploadResult);

        // Extract the image data from the response
        const imageData =
          uploadResult?.data?.uploadToBucket ||
          uploadResult?.uploadToBucket ||
          [];

        if (!Array.isArray(imageData) || imageData.length === 0) {
          throw new Error("No image data received from upload API");
        }

        // Prepare submission payload with uploaded image data
        const payload = {
          studentTestAttemptId: studentAttemptId,
          writingTestId: writingTestId,
          isAiBased: isAiBased,
          isPictureBased: true,
          writingImage: imageData,
        };

        console.log("Submitting writing answer with images:", payload);

        // Submit with image data
        const result = await createWritingAnswer(payload).unwrap();
        console.log("Writing submission successful:", result);

        // After successful submission, fetch test attempt results only if needed
        if (shouldFetchResults) {
          console.log("Fetching test attempt results for:", studentAttemptId);
          try {
            const resultsResponse = await getTestAttemptResults(
              studentAttemptId
            ).unwrap();
            console.log("Test attempt results:", resultsResponse);
            if (onResultsReceived) {
              onResultsReceived(resultsResponse);
            }
          } catch (resultsError) {
            console.error("Error fetching test attempt results:", resultsError);
          }
        } else {
          console.log(
            "Skipping test results fetch - not needed for this submission"
          );
        }
      }

      // ✅ For SIMULATION mode: update test attempt completion status
      if (mode === "simulation" && updateTestAttempt && studentAttemptId) {
        try {
          console.log(
            "Updating test attempt completion for simulation mode:",
            studentAttemptId
          );
          await updateTestAttempt({
            studentAttemptId,
            isCompleted: true,
          });
          console.log("Test attempt updated successfully");
        } catch (error) {
          console.error("Error updating test attempt:", error);
          // Don't fail the submission if this update fails
        }
      }

      // ✅ For PRACTICE mode: update test attempt completion status
      if (mode === "practice" && updateTestAttempt && studentAttemptId) {
        try {
          console.log(
            "Updating test attempt completion for practice mode:",
            studentAttemptId
          );
          await updateTestAttempt({
            studentAttemptId,
            isCompleted: true,
          });
          console.log("Practice test attempt updated successfully");
        } catch (error) {
          console.error("Error updating practice test attempt:", error);
          // Don't fail the submission if this update fails
        }
      }

      // ✅ Only for PRACTICE mode: call onFinish after successful submit
      if (mode === "practice" && isLastQuestion && onFinish) {
        onFinish();
      }

      setTimeout(() => {
        clearInterval(intervalRef.current);
        setUploadProgress(100);
        setTimeout(() => {
          setUploading(false);
        }, 300);
      }, 1000);
    } catch (error) {
      console.error("Error submitting writing answer:", error);
      clearInterval(intervalRef.current);
      setUploading(false);
      setSubmissionError(error?.data?.message || "Failed to submit writing answer. Please try again.");
    }
  }, [
    submissionType,
    studentAttemptId,
    writingTestId,
    isAiBased,
    answer,
    shouldFetchResults,
    onResultsReceived,
    createWritingAnswer,
    getTestAttemptResults,
    uploadedImages,
    uploadWritingImages,
    mode,
    updateTestAttempt,
    isLastQuestion,
    onFinish,
  ]);

  // Expose submission function to parent component
  useImperativeHandle(
    ref,
    () => ({
      submitAnswer: handleConfirmSubmit,
    }),
    [handleConfirmSubmit]
  );

  // Timer effect - moved after handleConfirmSubmit to avoid hoisting issues
  useEffect(() => {
    if (!adjustedEndTime) return;

    const updateApiTimer = () => {
      if (timerPaused || autoSubmitted) return; // Don't update when paused or already submitted

      const now = new Date();
      const endDateTime = new Date(adjustedEndTime);
      const remainingMs = endDateTime.getTime() - now.getTime();
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      if (remainingSeconds <= 0) {
        // Notify parent that time is up
        if (onTimeUp) {
          onTimeUp();
        }

        // Auto-trigger the "Finish Mock" button
        if (answer && answer.trim().length > 0) {
          console.log("Timer ended - auto-triggering Finish Mock button");
          setAutoSubmitted(true); // Prevent repeated calls
          handleConfirmSubmit();
        }
      }
    };

    // Update immediately
    updateApiTimer();

    // Then update every second
    const interval = setInterval(updateApiTimer, 1000);

    // Listen for pause timer event
    const handlePauseTimer = () => {
      setTimerPaused(true);
    };

    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [adjustedEndTime, onTimeUp, timerPaused, answer, handleConfirmSubmit, autoSubmitted]);

  const [showTask] = useState(true);

  // Update the adjustedEndTime when endTime changes
  useEffect(() => {
    setAdjustedEndTime(endTime);
  }, [endTime]);

  // Enhanced skip preparation handler
  const handleSkipPrep = () => {
    if (!prepActive) return;

    const timeSkipped = prepRemaining; // How much time was skipped
    setSkippedTime(timeSkipped);
    setPrepActive(false);

    // Adjust the end time by subtracting the skipped preparation time
    if (adjustedEndTime) {
      const currentEndTime = new Date(adjustedEndTime);
      const newEndTime = new Date(
        currentEndTime.getTime() - timeSkipped * 1000
      );
      const newEndTimeISO = newEndTime.toISOString();
      setAdjustedEndTime(newEndTimeISO);

      // Notify parent component about the adjusted end time
      if (onEndTimeAdjustment) {
        console.log(
          "📤 WritingSection: Calling parent callback with adjusted end time:",
          newEndTimeISO
        );
        onEndTimeAdjustment(newEndTimeISO);
        console.log("📤 WritingSection: Parent callback called successfully");
      } else {
        console.warn(
          "⚠️ WritingSection: onEndTimeAdjustment callback not provided"
        );
      }

      console.log(
        `Preparation time skipped: ${Math.floor(timeSkipped / 60)}m ${
          timeSkipped % 60
        }s`
      );
      console.log(`Original end time: ${currentEndTime.toISOString()}`);
      console.log(`New end time: ${newEndTimeISO}`);
    }
  };

  // Enhanced preparation time countdown
  useEffect(() => {
    if (!prepActive || isSubmitted) return; // Stop timer if already submitted
    if (prepRemaining <= 0) {
      setPrepActive(false);
      return;
    }
    const interval = setInterval(() => {
      setPrepRemaining((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [prepActive, prepRemaining, isSubmitted]);

  const fmtPrepClock = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  // Timer calculation logic similar to ReadingSection
  const getWritingTimeRemaining = () => {
    if (!adjustedEndTime) return 0;
    const now = new Date();
    const endDateTime = new Date(adjustedEndTime);
    const remainingMs = endDateTime.getTime() - now.getTime();
    return Math.max(0, Math.floor(remainingMs / 1000));
  };

  const getWritingTotalTime = () => {
    if (!startTime || !adjustedEndTime) return 1;
    const startDateTime = new Date(startTime);
    const endDateTime = new Date(adjustedEndTime);
    return Math.max(
      1,
      Math.floor((endDateTime.getTime() - startDateTime.getTime()) / 1000)
    );
  };

  const writingTimeRemaining = getWritingTimeRemaining();
  const writingTotalTime = getWritingTotalTime();
  const timerProgress =
    writingTotalTime > 0
      ? ((writingTotalTime - writingTimeRemaining) / writingTotalTime) * 100
      : 0;

  // Format timer display
  const formatTimerDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header Section */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          display: headerVisible ? "block" : "none",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            mb: 1,
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              gridTemplateAreas: {
                xs: `"left" "center" "right"`,
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 2fr 1fr",
                md: "1fr 2fr 1fr",
                lg: "1fr 2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" },
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 },
              rowGap: { xs: 0.3, sm: 0 },
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 },
            }}
          >
            {/* LEFT - Writing Info */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 },
                py: { xs: 0.1, sm: 0.2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.6rem", sm: "0.85rem", md: "0.95rem" },
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Writing Module
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#7a7a7a",
                    fontSize: { xs: "0.55rem", sm: "0.75rem", md: "0.8rem" },
                    lineHeight: 1.1,
                    letterSpacing: 0.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <b>Writing Task</b>
                </Typography>
              </Box>
            </Box>

            {/* CENTER - Writing Title and Time Information */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.8rem", sm: "1rem", md: "1.1rem" },
                    mb: 0.5,
                  }}
                >
                  TIME ALLOWED
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 0.2, sm: 1 },
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#7a7a7a",
                      fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    }}
                  >
                    READING TIME: {PREP_TIME_MINUTES} MINUTES
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#7a7a7a",
                      fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    }}
                  >
                    WRITING TIME: 40 MINUTES
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Circle Clock, Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={timerProgress}
                  showPie
                  showRing
                  idSuffix="writingTime"
                />
              </Box>

              {/* Timer and Notes Row - Below circle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: { xs: 16, sm: 20, md: 24 },
                      color: "#9D2235",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {formatTimerDisplay(writingTimeRemaining)}
                  </Typography>
                </Box>

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen(true)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow:
                          "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: 38,
                      height: 38,
                      minWidth: 38,
                      minHeight: 38,
                      p: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StickyNote2Icon
                      sx={{
                        fontSize: 18,
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* ===== Split‑screen ===== */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* left: case notes */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            borderRight: { md: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
            background: "#fff",
            position: "relative",
          }}
        >
          {/* Zoom Controls - Left Panel */}
          <Box
            sx={{
              position: "sticky",
              top: 8,
              right: 8,
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
              gap: 1,
            }}
          >
            <Tooltip title="Zoom Out" placement="top">
              <IconButton
                onClick={handleLeftZoomOut}
                disabled={leftZoom <= 0.8}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomOut sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{
                alignSelf: "center",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#666",
                minWidth: "50px",
                textAlign: "center",
                bgcolor: "rgba(157, 34, 53, 0.05)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              {Math.round(leftZoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In" placement="top">
              <IconButton
                onClick={handleLeftZoomIn}
                disabled={leftZoom >= 1.5}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomIn sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              transform: `scale(${leftZoom})`,
              transformOrigin: "top left",
              transition: "transform 0.2s ease",
            }}
          >
            <TextHighlighterWrapper onAddNote={handleAddNote}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Case Notes
              </Typography>
              {caseNotes.map((note, idx) => (
                <Card
                  key={note._id || idx}
                  elevation={2}
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 1.5, sm: 3 },
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                    background: "#fff",
                  }}
                >
                  {note.title && (
                    <HtmlRenderer
                      content={note.title}
                      component="h6"
                      sx={{
                        fontWeight: 700,
                        mb: { xs: 1.5, sm: 2 },
                        color: "#9D2235",
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    />
                  )}

                  {(!note.subSections || note.subSections.length === 0) &&
                    note.content && (
                      <HtmlRenderer
                        content={note.content}
                        component="div"
                        sx={{
                          mb: { xs: 1.5, sm: 2 },
                          lineHeight: { xs: 1.6, sm: 1.8 },
                          whiteSpace: "pre-line",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      />
                    )}

                  {note.subSections?.length > 0 && (
                    <Box sx={{ ml: 2, mb: 1 }}>
                      {note.subSections.length > 1 ? (
                        <Box component="ul" sx={{ listStyle: "disc", pl: 5 }}>
                          {note.subSections.map((sub, sidx) => (
                            <Box
                              component="li"
                              key={sub._id || sidx}
                              sx={{ mb: 1 }}
                            >
                              {sub.label && (
                                <HtmlRenderer
                                  content={`${sub.label}${
                                    sub.label ? ": " : ""
                                  }`}
                                  component="span"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  }}
                                />
                              )}
                              {sub.content && (
                                <HtmlRenderer
                                  content={sub.content}
                                  component="span"
                                  sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  }}
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box>
                          {note.subSections[0].label && (
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                              }}
                            >
                              {note.subSections[0].label}
                              {note.subSections[0].label ? ": " : ""}
                            </Typography>
                          )}
                          {note.subSections[0].content && (
                            <HtmlRenderer
                              content={note.subSections[0].content}
                              component="div"
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Card>
              ))}
            </TextHighlighterWrapper>
          </Box>
        </Box>

        {/* right: writing task */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            pl: { xs: 1, sm: 2 },
            overflow: "auto",
            background: "#f8f9fa",
            position: "relative",
          }}
        >
          {/* Zoom Controls - Right Panel */}
          <Box
            sx={{
              position: "sticky",
              top: 8,
              right: 8,
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
              gap: 1,
            }}
          >
            <Tooltip title="Zoom Out" placement="top">
              <IconButton
                onClick={handleRightZoomOut}
                disabled={rightZoom <= 0.8}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomOut sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{
                alignSelf: "center",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#666",
                minWidth: "50px",
                textAlign: "center",
                bgcolor: "rgba(157, 34, 53, 0.05)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              {Math.round(rightZoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In" placement="top">
              <IconButton
                onClick={handleRightZoomIn}
                disabled={rightZoom >= 1.5}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomIn sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              transform: `scale(${rightZoom})`,
              transformOrigin: "top left",
              transition: "transform 0.2s ease",
            }}
          >
            {/* Instructions Card - WITH TextHighlighter */}
            <TextHighlighterWrapper onAddNote={handleAddNote}>
              <Card
                elevation={2}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: 2,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                  background: "#fff",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  Writing Task
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 1.5, sm: 2 },
                    color: "#9D2235",
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  Instructions
                </Typography>

                {/* Instruction text */}
                <Typography
                  variant="body1"
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    lineHeight: { xs: 1.6, sm: 1.8 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    fontWeight: 500,
                    color: "#333",
                  }}
                >
                  Read the case notes below and complete the writing task which
                  follows.
                </Typography>

                {showTask && (
                  <HtmlRenderer
                    content={writingPrompt}
                    component="div"
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      lineHeight: { xs: 1.6, sm: 1.8 },
                      whiteSpace: "pre-line",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  />
                )}
              </Card>
            </TextHighlighterWrapper>

            {/* Answer Input Card - WITHOUT TextHighlighter to prevent touch blocking */}
            <Card
              elevation={2}
              sx={{
                mb: { xs: 2, sm: 3 },
                p: { xs: 1.5, sm: 3 },
                borderRadius: 2,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                background: "#fff",
              }}
            >
                {/* Preparation Time Display */}
                {prepActive && !isSubmitted && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: { xs: 2, sm: 3 },
                      p: { xs: 2, sm: 3 },
                      bgcolor: "#f5f5f5",
                      borderRadius: 2,
                      // Enhanced mobile support
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                      position: "relative",
                      isolation: "isolate", // Create stacking context for better mobile rendering
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ width: 20, height: 20, color: "#9D2235" }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 11V7a4 4 0 118 0v4M6 11V7a6 6 0 1112 0v4m-6 4v2m0 0h.01"
                        />
                      </svg>
                      <Typography
                        sx={{
                          fontSize: { xs: "1rem", sm: "1.125rem" },
                          fontWeight: 700,
                          color: "#9D2235",
                        }}
                      >
                        Preparation Time
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.5rem", sm: "2rem" },
                        fontFamily: "monospace",
                        fontWeight: 700,
                        color: "#9D2235",
                        mb: 2,
                      }}
                    >
                      {fmtPrepClock(prepRemaining)}
                    </Typography>
                    <Button
                      onClick={handleSkipPrep}
                      disabled={isSubmitted}
                      onTouchStart={(e) => {
                        // Improve mobile touch response
                        e.stopPropagation();
                        console.log("Skip Preparation button touched");
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        console.log("Skip Preparation button touch ended");
                      }}
                      variant="contained"
                      sx={{
                        bgcolor: "#9D2235",
                        "&:hover": { bgcolor: "#7a182a" },
                        "&:active": {
                          bgcolor: "#5d1220",
                          transform: "scale(0.98)",
                        },
                        fontWeight: 700,
                        py: { xs: 1, sm: 1.5 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        // Enhanced mobile touch support
                        minHeight: { xs: 48, sm: 56 }, // Minimum touch target size for mobile
                        minWidth: { xs: 120, sm: "auto" },
                        touchAction: "manipulation", // Prevents zoom on double-tap
                        WebkitTapHighlightColor: "transparent", // Removes blue highlight on mobile
                        cursor: "pointer",
                        userSelect: "none", // Prevents text selection
                        // Ensure proper layering on mobile
                        position: "relative",
                        zIndex: 1,
                        // Add subtle shadow for better visual feedback
                        boxShadow: "0 2px 8px rgba(157, 34, 53, 0.2)",
                        transition: "all 0.2s ease-in-out",
                      }}
                      startIcon={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ width: 20, height: 20 }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 11V7a4 4 0 118 0v4M6 11V7a6 6 0 1112 0v4m-6 4v2m0 0h.01"
                          />
                        </svg>
                      }
                    >
                      Skip Preparation
                    </Button>
                  </Box>
                )}

                {/* Already Submitted Message */}
                {isSubmitted && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: { xs: 2, sm: 3 },
                      p: { xs: 2, sm: 3 },
                      bgcolor: "#e3f2fd",
                      borderRadius: 2,
                      border: "1px solid #90caf9",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ width: 20, height: 20, color: "#1976d2" }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          fontWeight: 600,
                          color: "#1976d2",
                        }}
                      >
                        Test Already Submitted
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        color: "#1565c0",
                        textAlign: "center",
                      }}
                    >
                      This test has been completed and submitted. No further changes can be made.
                    </Typography>
                  </Box>
                )}

                {!prepActive && skippedTime > 0 && !isSubmitted && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: { xs: 2, sm: 3 },
                      p: { xs: 2, sm: 3 },
                      bgcolor: "#fff3cd",
                      borderRadius: 2,
                      border: "1px solid #ffeaa7",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ width: 16, height: 16, color: "#856404" }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          fontWeight: 600,
                          color: "#856404",
                        }}
                      >
                        Time Adjustment Notice
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        color: "#856404",
                        textAlign: "center",
                      }}
                    >
                      Preparation time skipped: {Math.floor(skippedTime / 60)}m{" "}
                      {skippedTime % 60}s
                      <br />
                      This time has been deducted from your total writing time.
                    </Typography>
                  </Box>
                )}

                {/* Submission Error Display */}
                {submissionError && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: { xs: 2, sm: 3 },
                      p: { xs: 2, sm: 3 },
                      bgcolor: "#ffebee",
                      borderRadius: 2,
                      border: "2px solid #f44336",
                      boxShadow: "0 2px 8px rgba(244, 67, 54, 0.2)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ width: 24, height: 24, color: "#d32f2f", flexShrink: 0 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          fontWeight: 600,
                          color: "#d32f2f",
                          mb: 0.5,
                        }}
                      >
                        Submission Failed
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          color: "#c62828",
                        }}
                      >
                        {submissionError}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => setSubmissionError(null)}
                      size="small"
                      sx={{
                        color: "#d32f2f",
                        "&:hover": {
                          bgcolor: "rgba(211, 47, 47, 0.1)",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {/* Writing Area */}
                {submissionType === "computer" && mode !== "readonly" && (
                  <Box 
                    sx={{ 
                      position: "relative",
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    <TextField
                      multiline
                      rows={12}
                      fullWidth
                      value={answer}
                      onChange={(e) => onAnswerChange(e.target.value)}
                      placeholder="Type your answer..."
                      disabled={prepActive || isSubmitted}
                      inputProps={{
                        style: {
                          WebkitUserSelect: "text",
                          WebkitTouchCallout: "default",
                          touchAction: "manipulation",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          borderRadius: 2,
                          paddingBottom: "40px",
                          backgroundColor: prepActive || isSubmitted ? "#f5f5f5" : "#fff",
                          cursor: prepActive || isSubmitted ? "not-allowed" : "text",
                          position: "relative",
                          zIndex: 1,
                          transition: "all 0.2s ease-in-out",
                          touchAction: "manipulation",
                        },
                        "& .MuiOutlinedInput-input": {
                          paddingBottom: "8px",
                          touchAction: "manipulation",
                          "&:disabled": {
                            WebkitTextFillColor: "#999",
                            color: "#999",
                          },
                          "&:not(:disabled)": {
                            cursor: "text",
                            userSelect: "text",
                            WebkitUserSelect: "text",
                          },
                        },
                        "& textarea": {
                          touchAction: "manipulation !important",
                          WebkitUserSelect: "text !important",
                          userSelect: "text !important",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        right: 16,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        bgcolor: "rgba(255,255,255,0.95)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        zIndex: 0,
                        pointerEvents: "none",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        border: "1px solid rgba(0,0,0,0.1)",
                        fontWeight: 500,
                      }}
                    >
                      {answer.trim().split(/\s+/).filter(Boolean).length}/200
                      words
                    </Typography>
                    {prepActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            width: 24,
                            height: 24,
                            color: "#9D2235",
                            opacity: 0.8,
                          }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 11V7a4 4 0 118 0v4M6 11V7a6 6 0 1112 0v4m-6 4v2m0 0h.01"
                          />
                        </svg>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Paper-based submission */}
                {submissionType === "paper" && (
                  <Box sx={{ textAlign: "center", py: { xs: 2, sm: 4 } }}>
                    <Typography
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                        fontWeight: 600,
                        color: "#9D2235",
                        mb: 2,
                      }}
                    >
                      Paper-Based Submission
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        color: "text.secondary",
                        mb: 2,
                      }}
                    >
                      Please write your answer on paper and upload images when
                      complete.
                    </Typography>

                    {/* Preparation Time Warning */}
                    {prepActive && (
                      <Box
                        sx={{
                          p: 2,
                          mb: 3,
                          bgcolor: "#fff3cd",
                          border: "1px solid #ffeaa7",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            color: "#856404",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          📝 Image upload will be available after preparation
                          time ends or is skipped
                        </Typography>
                      </Box>
                    )}

                    {uploadedImages.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                          Uploaded Images:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          {uploadedImages.map((img, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                position: "relative",
                                width: 100,
                                height: 100,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={img.preview}
                                alt={`Upload ${idx + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <IconButton
                                onClick={() => handleRemoveImage(idx)}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  console.log(
                                    `Remove image ${idx} button touched`
                                  );
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  console.log(
                                    `Remove image ${idx} button touch ended`
                                  );
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  backgroundColor: "rgba(157, 34, 53, 0.8)",
                                  color: "white",
                                  width: { xs: 24, sm: 20 }, // Slightly larger on mobile
                                  height: { xs: 24, sm: 20 },
                                  minWidth: { xs: 24, sm: 20 },
                                  "&:hover": {
                                    backgroundColor: "rgba(157, 34, 53, 1)",
                                  },
                                  "&:active": {
                                    backgroundColor: "rgba(122, 24, 42, 1)",
                                    transform: "scale(0.95)",
                                  },
                                  // Enhanced mobile support
                                  touchAction: "manipulation",
                                  WebkitTapHighlightColor: "transparent",
                                  userSelect: "none",
                                  zIndex: 2,
                                  transition: "all 0.2s ease-in-out",
                                }}
                                size="small"
                              >
                                <CloseIcon
                                  sx={{ fontSize: { xs: 14, sm: 12 } }}
                                />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                      id="image-upload"
                      disabled={prepActive || isSubmitted}
                    />
                    <label
                      htmlFor="image-upload"
                      style={{
                        display: "inline-block",
                        cursor: (prepActive || isSubmitted) ? "not-allowed" : "pointer",
                        touchAction: "manipulation", // Prevent zoom on double-tap
                        WebkitTapHighlightColor: "transparent", // Remove blue highlight
                      }}
                      onTouchStart={(e) => {
                        if (!prepActive && !isSubmitted) {
                          console.log("Image upload button touched");
                          e.stopPropagation();
                        }
                      }}
                      onTouchEnd={(e) => {
                        if (!prepActive && !isSubmitted) {
                          console.log("Image upload button touch ended");
                          e.stopPropagation();
                        }
                      }}
                    >
                      <Button
                        component="span"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={prepActive || isSubmitted}
                        sx={{
                          bgcolor: (prepActive || isSubmitted) ? "#cccccc" : "#9D2235",
                          color: (prepActive || isSubmitted) ? "#666666" : "white",
                          "&:hover": {
                            bgcolor: (prepActive || isSubmitted) ? "#cccccc" : "#7a182a",
                          },
                          "&:active": {
                            bgcolor: (prepActive || isSubmitted) ? "#cccccc" : "#5d1220",
                            transform: (prepActive || isSubmitted) ? "none" : "scale(0.98)",
                          },
                          "&:disabled": {
                            bgcolor: "#cccccc",
                            color: "#666666",
                          },
                          cursor: (prepActive || isSubmitted) ? "not-allowed" : "pointer",
                          // Enhanced mobile support
                          minHeight: { xs: 48, sm: 56 }, // Minimum touch target size
                          minWidth: { xs: 140, sm: "auto" },
                          touchAction: "manipulation",
                          WebkitTapHighlightColor: "transparent",
                          userSelect: "none",
                          position: "relative",
                          zIndex: 1,
                          boxShadow: (prepActive || isSubmitted)
                            ? "none"
                            : "0 2px 8px rgba(157, 34, 53, 0.2)",
                          transition: "all 0.2s ease-in-out",
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 2, sm: 3 },
                          borderRadius: 3,
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          fontWeight: 600,
                        }}
                      >
                        {isSubmitted
                          ? "Already Submitted"
                          : prepActive
                          ? "Upload Available After Preparation"
                          : "Select Images"}
                      </Button>
                    </label>
                  </Box>
                )}
              </Card>
          </Box>
        </Box>
      </Box>

      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        notes={notes}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 3,
            p: 3,
            width: "100%",
            maxWidth: 400,
            mx: "auto",
            mt: 24,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2, color: "#9D2235" }}
          >
            Finish Writing Test
          </Typography>
          <Typography sx={{ mb: 4, color: "text.secondary" }}>
            Are you sure you want to finish and submit your answer? This action
            cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              variant="outlined"
              sx={{
                px: 2,
                py: 1,
                borderColor: "#e0e0e0",
                color: "text.secondary",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              variant="contained"
              sx={{
                px: 2,
                py: 1,
                bgcolor: "#9D2235",
                "&:hover": { bgcolor: "#7a182a" },
              }}
            >
              Finish
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Upload Progress */}
      <Backdrop open={uploading} sx={{ zIndex: 9999 }}>
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 3,
            p: 3,
            width: "100%",
            maxWidth: 400,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2, color: "#9D2235" }}
          >
            {submissionType === "paper"
              ? "Uploading Images..."
              : "Submitting Answer..."}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: 8,
              bgcolor: "#e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box
              sx={{
                height: "100%",
                bgcolor: "#9D2235",
                borderRadius: 1,
                width: `${uploadProgress}%`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
          <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {uploadProgress}%
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
});
